import { useCallback, useEffect, useState } from 'react';
import type { OnboardingStep } from '../services/types';

export interface SuperbarEventPayload {
  message?: string;
  sessionId?: string;
  onboardingStep?: OnboardingStep;
  timestamp: string;
}

export interface SuperbarEventDispatcher {
  dispatchMessageSent: (
    payload: Omit<SuperbarEventPayload, 'timestamp'>
  ) => void;
  dispatchMessageReceived: (
    payload: Omit<SuperbarEventPayload, 'timestamp'>
  ) => void;
  dispatchSessionAvailable: () => void;
}

export function useSuperbarEventDispatcher(): SuperbarEventDispatcher {
  const [isReady, setIsReady] = useState(false);
  const [queuedEvents, setQueuedEvents] = useState<
    Array<{ eventType: string; payload: SuperbarEventPayload }>
  >([]);

  useEffect(() => {
    const readyHandler = (event: CustomEvent) => {
      if (event.detail?.type === 'SUPERBAR_EVENT_LISTENER_READY') {
        setIsReady(true);
      }
    };

    window.addEventListener(
      'SUPERBAR_EVENT_LISTENER_READY',
      readyHandler as EventListener
    );

    return () => {
      window.removeEventListener(
        'SUPERBAR_EVENT_LISTENER_READY',
        readyHandler as EventListener
      );
    };
  }, []);

  useEffect(() => {
    if (isReady && queuedEvents.length > 0) {
      // Copy events before clearing to avoid re-triggering this effect
      const eventsToProcess = [...queuedEvents];
      setQueuedEvents([]);

      eventsToProcess.forEach(({ eventType, payload }) => {
        const eventData = {
          type: eventType,
          payload: {
            ...payload,
            timestamp: new Date().toISOString(),
          },
        };

        window.dispatchEvent(
          new CustomEvent('SUPERBAR_EVENT', {
            detail: eventData,
          })
        );
      });
    }
  }, [isReady, queuedEvents]);

  const dispatchEvent = useCallback(
    (eventType: string, payload: SuperbarEventPayload) => {
      if (isReady) {
        const eventData = {
          type: eventType,
          payload: {
            ...payload,
            timestamp: new Date().toISOString(),
          },
        };

        window.dispatchEvent(
          new CustomEvent('SUPERBAR_EVENT', {
            detail: eventData,
          })
        );
        return;
      }

      setQueuedEvents((prev) => {
        const newQueue = [...prev, { eventType, payload }];
        return newQueue.length > 3 ? newQueue.slice(-3) : newQueue;
      });
    },
    [isReady]
  );

  const dispatchMessageSent = useCallback(
    (payload: Omit<SuperbarEventPayload, 'timestamp'>) => {
      // dispatchEvent will add the timestamp, no need to add it here
      dispatchEvent(
        'SUPERBAR_CHAT_MESSAGE_SENT',
        payload as SuperbarEventPayload
      );
    },
    [dispatchEvent]
  );

  const dispatchMessageReceived = useCallback(
    (payload: Omit<SuperbarEventPayload, 'timestamp'>) => {
      // dispatchEvent will add the timestamp, no need to add it here
      dispatchEvent(
        'SUPERBAR_CHAT_MESSAGE_RECEIVED',
        payload as SuperbarEventPayload
      );
    },
    [dispatchEvent]
  );

  const dispatchSessionAvailable = useCallback(() => {
    dispatchEvent('SUPERBAR_SESSION_AVAILABLE', {
      timestamp: new Date().toISOString(),
    });
  }, [dispatchEvent]);

  return {
    dispatchMessageSent,
    dispatchMessageReceived,
    dispatchSessionAvailable,
  };
}
